import { Texture } from 'pixi.js'

/**
 * All light is drawn from three white textures that get tinted per bulb.
 * Tinting one texture is far cheaper than drawing a gradient per bulb, which
 * matters when a big house carries a couple of thousand bulbs on an iPad.
 */

let glowTex: Texture | null = null
let coreTex: Texture | null = null
let spillTex: Texture | null = null

const canvas = (size: number) => {
  const c = document.createElement('canvas')
  c.width = size
  c.height = size
  return c
}

/**
 * The bloom around a bulb. Drawn additively, so overlapping bulbs pool into
 * real brightness instead of stacking into gray. The falloff curve matters
 * more than the radius: a linear gradient reads as a sticker, this reads as light.
 */
export function getGlowTexture(): Texture {
  if (glowTex) return glowTex
  const size = 160
  const c = canvas(size)
  const ctx = c.getContext('2d')!
  const r = size / 2
  const g = ctx.createRadialGradient(r, r, 0, r, r, r)
  /*
   * MEASURED, not invented.
   *
   * These stops come from a real C9 on a white fascia in one of our own night
   * photos: core clipped at 245, wall ambient at 35, and the halo reaching that
   * ambient at about eight core radii out. Sampled radially, the real falloff
   * is FAR slower than the curve that was here before, which had dropped to 5%
   * by 3 core radii while the photograph was still at 33%. That is why our
   * bulbs read as dots with a tight ring and a real one reads as a lamp.
   *
   * Half brightness lands at 1.8 core radii, matching the measurement.
   */
  g.addColorStop(0.0, 'rgba(255,255,255,1)')
  g.addColorStop(0.15, 'rgba(255,255,255,0.85)')
  g.addColorStop(0.225, 'rgba(255,255,255,0.6)')
  g.addColorStop(0.3, 'rgba(255,255,255,0.4)')
  g.addColorStop(0.375, 'rgba(255,255,255,0.33)')
  g.addColorStop(0.45, 'rgba(255,255,255,0.24)')
  g.addColorStop(0.6, 'rgba(255,255,255,0.19)')
  g.addColorStop(0.75, 'rgba(255,255,255,0.1)')
  g.addColorStop(0.9, 'rgba(255,255,255,0.02)')
  g.addColorStop(1.0, 'rgba(255,255,255,0)')
  ctx.fillStyle = g
  ctx.fillRect(0, 0, size, size)
  glowTex = Texture.from(c)
  return glowTex
}

/**
 * The glass body with a hot filament core and a specular highlight.
 * A flat dot is the single biggest giveaway that a mockup is generated.
 */
/**
 * A C9, not a dot.
 *
 * This was an ellipse, and a roofline of ellipses reads as a string of glowing
 * beads - which is what gave the mockups away at any real zoom. A C9 is a
 * specific and very recognizable object: a faceted strawberry, widest about a
 * third up from the base, tapering to a blunt rounded tip, sitting in a socket
 * collar. The facets are the important part. They are why a real C9 has bright
 * and dim patches around its circumference instead of one even glow, and why a
 * roofline of them sparkles as you drive past instead of sitting there flat.
 *
 * Drawn pointing DOWN the texture, so the renderer's existing per-bulb angle
 * hangs it the right way on a fascia and lets it lie along a wrapped trunk.
 */
export function getCoreTexture(): Texture {
  if (coreTex) return coreTex
  const size = 96
  const c = canvas(size)
  const ctx = c.getContext('2d')!
  const cx = size / 2
  // The glass occupies the lower part; the collar sits above it.
  const top = size * 0.20
  const bot = size * 0.94
  const w = size * 0.30

  /** The C9 silhouette: shoulder, belly, taper, blunt tip. */
  const outline = () => {
    ctx.beginPath()
    ctx.moveTo(cx - w * 0.52, top)
    // shoulder flaring out to the widest point
    ctx.bezierCurveTo(cx - w * 1.0, top + (bot - top) * 0.16, cx - w, top + (bot - top) * 0.34, cx - w * 0.94, top + (bot - top) * 0.52)
    // taper down to the tip
    ctx.bezierCurveTo(cx - w * 0.86, top + (bot - top) * 0.82, cx - w * 0.44, bot, cx, bot)
    ctx.bezierCurveTo(cx + w * 0.44, bot, cx + w * 0.86, top + (bot - top) * 0.82, cx + w * 0.94, top + (bot - top) * 0.52)
    ctx.bezierCurveTo(cx + w, top + (bot - top) * 0.34, cx + w * 1.0, top + (bot - top) * 0.16, cx + w * 0.52, top)
    ctx.closePath()
  }

  // Glass body. Brightest at the filament, which sits low in a C9, not centerd.
  const fy = top + (bot - top) * 0.46
  const body = ctx.createRadialGradient(cx, fy, w * 0.06, cx, fy, w * 1.5)
  body.addColorStop(0, 'rgba(255,255,255,1)')
  body.addColorStop(0.3, 'rgba(255,255,255,0.96)')
  body.addColorStop(0.68, 'rgba(255,255,255,0.74)')
  body.addColorStop(1, 'rgba(255,255,255,0.1)')
  ctx.save()
  outline()
  ctx.clip()
  ctx.fillStyle = body
  ctx.fillRect(0, 0, size, size)

  /*
   * The facets. Vertical flutes running down the glass, alternately catching
   * and shading. Kept subtle - they should register as texture at a distance
   * and as facets close up, never as stripes.
   */
  for (let i = -3; i <= 3; i++) {
    const fx = cx + i * w * 0.30
    const g = ctx.createLinearGradient(fx - w * 0.15, 0, fx + w * 0.15, 0)
    const strong = i % 2 === 0
    g.addColorStop(0, 'rgba(255,255,255,0)')
    g.addColorStop(0.5, `rgba(255,255,255,${strong ? 0.16 : 0})`)
    g.addColorStop(1, 'rgba(0,0,0,0.05)')
    ctx.fillStyle = g
    ctx.fillRect(fx - w * 0.18, top, w * 0.36, bot - top)
  }

  // Specular on the shoulder, upper left. This is what reads as glass.
  const hl = ctx.createRadialGradient(
    cx - w * 0.42, top + (bot - top) * 0.26, 0,
    cx - w * 0.42, top + (bot - top) * 0.26, w * 0.55,
  )
  hl.addColorStop(0, 'rgba(255,255,255,0.9)')
  hl.addColorStop(1, 'rgba(255,255,255,0)')
  ctx.fillStyle = hl
  ctx.beginPath()
  ctx.arc(cx - w * 0.42, top + (bot - top) * 0.26, w * 0.55, 0, Math.PI * 2)
  ctx.fill()
  ctx.restore()

  // The socket collar. Dark, so it separates the bulb from the wire rather
  // than letting the glow run straight into it.
  const col = ctx.createLinearGradient(0, top - size * 0.1, 0, top + size * 0.02)
  col.addColorStop(0, 'rgba(255,255,255,0.10)')
  col.addColorStop(1, 'rgba(255,255,255,0.34)')
  ctx.fillStyle = col
  ctx.fillRect(cx - w * 0.5, top - size * 0.10, w, size * 0.12)

  coreTex = Texture.from(c)
  return coreTex
}

/**
 * The wash a strand throws onto the fascia behind it. Wide, very soft, very low
 * alpha. This is the cue almost every competitor skips, and it is the difference
 * between lights that sit on the photo and lights that sit on the house.
 */
export function getSpillTexture(): Texture {
  if (spillTex) return spillTex
  const size = 256
  const c = canvas(size)
  const ctx = c.getContext('2d')!
  const r = size / 2
  const g = ctx.createRadialGradient(r, r, 0, r, r, r)
  g.addColorStop(0.0, 'rgba(255,255,255,0.34)')
  g.addColorStop(0.22, 'rgba(255,255,255,0.16)')
  g.addColorStop(0.5, 'rgba(255,255,255,0.05)')
  g.addColorStop(0.78, 'rgba(255,255,255,0.012)')
  g.addColorStop(1.0, 'rgba(255,255,255,0)')
  ctx.fillStyle = g
  ctx.fillRect(0, 0, size, size)
  spillTex = Texture.from(c)
  return spillTex
}

let bounceTex: Texture | null = null
let veilTex: Texture | null = null
let streakTex: Texture | null = null

/**
 * Bounce: the light a bulb throws DOWN onto the surface it is mounted on.
 *
 * The existing spill is a symmetrical halo, which is what light in mid air
 * looks like. A bulb on a fascia is not in mid air. It faces out and down, and
 * the stucco under it picks up a soft warm wash that fades over a couple of
 * feet. That wash is the single strongest cue that the lights belong to the
 * building rather than sitting on top of the photograph, and it is the thing
 * the competitor has none of: on their render the wall directly beneath a
 * strand is exactly as dark as the wall ten feet away.
 *
 * The texture is anchored at the top so it hangs below the bulb, and it is
 * wider than it is tall because light spreads sideways along a wall faster
 * than it travels down it.
 */
export function getBounceTexture(): Texture {
  if (bounceTex) return bounceTex
  const size = 256
  const c = canvas(size)
  const ctx = c.getContext('2d')!
  const g = ctx.createRadialGradient(size / 2, 0, 0, size / 2, 0, size)
  g.addColorStop(0.0, 'rgba(255,255,255,0.5)')
  g.addColorStop(0.08, 'rgba(255,255,255,0.34)')
  g.addColorStop(0.22, 'rgba(255,255,255,0.15)')
  g.addColorStop(0.45, 'rgba(255,255,255,0.05)')
  g.addColorStop(0.72, 'rgba(255,255,255,0.013)')
  g.addColorStop(1.0, 'rgba(255,255,255,0)')
  ctx.fillStyle = g
  ctx.fillRect(0, 0, size, size)
  bounceTex = Texture.from(c)
  return bounceTex
}

/**
 * Veiling glare. Every real lens scatters a little light across the whole
 * frame, which is why a night photograph of a lit house has a faint wash
 * around the display rather than pin sharp dots on black. Very wide, very
 * faint, and drawn for only a fraction of the bulbs, because the effect is
 * cumulative and there is no need to pay for it on every one.
 */
export function getVeilTexture(): Texture {
  if (veilTex) return veilTex
  const size = 256
  const c = canvas(size)
  const ctx = c.getContext('2d')!
  const r = size / 2
  const g = ctx.createRadialGradient(r, r, 0, r, r, r)
  g.addColorStop(0.0, 'rgba(255,255,255,0.10)')
  g.addColorStop(0.3, 'rgba(255,255,255,0.045)')
  g.addColorStop(0.62, 'rgba(255,255,255,0.014)')
  g.addColorStop(1.0, 'rgba(255,255,255,0)')
  ctx.fillStyle = g
  ctx.fillRect(0, 0, size, size)
  veilTex = Texture.from(c)
  return veilTex
}

/**
 * The horizontal streak a bright point picks up through a real lens. Small,
 * subtle, and only on the bulbs bright enough to earn it. Overdone this looks
 * like a cheap filter; at this strength it reads as a photograph.
 */
let spikeTex: Texture | null = null

/**
 * A DIFFRACTION SPIKE, which is not the same thing as a streak.
 *
 * The streak texture is a soft bar - right for the gentle horizontal smear a
 * bright point picks up through any lens, and wrong for a star flare, which is
 * the opposite shape: a needle-thin line with a hot core that falls away fast
 * along its length and almost instantly across it. Reusing the streak for
 * sparkle is why the first attempt read as a smudge instead of a star.
 *
 * Drawn once, horizontally, and rotated per spike by the renderer.
 */
export function getSpikeTexture(): Texture {
  if (spikeTex) return spikeTex
  const w = 512
  const h = 16
  const c = canvas(w)
  c.height = h
  const ctx = c.getContext('2d')!
  const mid = h / 2

  for (let x = 0; x < w; x++) {
    // Distance from the center of the spike, 0 at the core and 1 at the tip.
    const u = Math.abs(x - w / 2) / (w / 2)
    // Falls away fast, then has a long faint tail. This is what makes a spike
    // read as light rather than as a drawn line.
    const along = Math.pow(1 - u, 3.2) * 0.82 + Math.pow(1 - u, 0.85) * 0.18
    for (let y = 0; y < h; y++) {
      const v = Math.abs(y - mid) / mid
      // Very tight across the spike, and tighter still away from the core, so
      // it tapers to a point instead of ending as a rectangle.
      const across = Math.pow(1 - v, 6 + 10 * u)
      const a = along * across
      if (a <= 0.002) continue
      ctx.fillStyle = `rgba(255,255,255,${Math.min(1, a)})`
      ctx.fillRect(x, y, 1, 1)
    }
  }
  spikeTex = Texture.from(c)
  return spikeTex
}

let glintTex: Texture | null = null

/**
 * A STAR GLINT. The Photoshop sparkle, not a lens artefact.
 *
 * The previous attempt at this modeled diffraction spikes - the faint smear a
 * stopped-down lens puts on a bright point - which is physically the right idea
 * and visually the wrong one. What people mean by sparkle is the graphic mark:
 * pure white, four needle-sharp rays that taper to nothing, a dense core, and
 * few enough of them that each one reads.
 *
 * Drawn as ONE texture rather than rotated spikes, because the shape depends on
 * how the rays meet at the middle. Four long, four short, and a hot core, which
 * is the classic sparkle and what makes it read at a glance.
 */
export function getGlintTexture(): Texture {
  const S = 256
  if (glintTex) return glintTex
  const c = canvas(S)
  const ctx = c.getContext('2d')!
  const mid = S / 2
  const img = ctx.createImageData(S, S)
  const d = img.data

  for (let y = 0; y < S; y++) {
    for (let x = 0; x < S; x++) {
      const dx = (x - mid) / mid
      const dy = (y - mid) / mid
      const r = Math.hypot(dx, dy)
      if (r > 1) continue
      const ang = Math.atan2(dy, dx)

      // Four long rays on the axes, four short ones on the diagonals.
      const long = Math.pow(Math.abs(Math.cos(ang * 2)), 90)
      const short = Math.pow(Math.abs(Math.cos(ang * 2 - Math.PI)), 140) * 0.34

      // Rays fade along their length; the falloff is what makes them taper to a
      // needle instead of ending as a spike of constant width.
      const fall = Math.pow(Math.max(0, 1 - r), 2.2)
      const rays = (long + short) * fall

      // A hot core, small and dense, which is what a glint reads from.
      const core = Math.pow(Math.max(0, 1 - r * 7.5), 2.4)
      // Just enough halo to seat the star rather than leaving it floating.
      const halo = Math.pow(Math.max(0, 1 - r * 2.6), 3.4) * 0.28

      const a = Math.min(1, rays + core + halo)
      if (a <= 0.004) continue
      const i = (y * S + x) * 4
      d[i] = 255
      d[i + 1] = 255
      d[i + 2] = 255
      d[i + 3] = Math.round(a * 255)
    }
  }
  ctx.putImageData(img, 0, 0)
  glintTex = Texture.from(c)
  return glintTex
}

export function getStreakTexture(): Texture {
  if (streakTex) return streakTex
  const w = 256
  const h = 32
  const c = document.createElement('canvas')
  c.width = w
  c.height = h
  const ctx = c.getContext('2d')!
  const g = ctx.createLinearGradient(0, 0, w, 0)
  g.addColorStop(0.0, 'rgba(255,255,255,0)')
  g.addColorStop(0.32, 'rgba(255,255,255,0.16)')
  g.addColorStop(0.5, 'rgba(255,255,255,0.75)')
  g.addColorStop(0.68, 'rgba(255,255,255,0.16)')
  g.addColorStop(1.0, 'rgba(255,255,255,0)')
  ctx.fillStyle = g
  // Taper the ends vertically so it is a streak, not a bar.
  for (let y = 0; y < h; y++) {
    const t = Math.abs(y - h / 2) / (h / 2)
    ctx.globalAlpha = Math.pow(1 - t, 2.4)
    ctx.fillRect(0, y, w, 1)
  }
  ctx.globalAlpha = 1
  streakTex = Texture.from(c)
  return streakTex
}
